import React, { useEffect, useMemo, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { themeColors } from '../../styles/theme';
import { hasFreshLevels, isCurrentNySessionDate, isValidPriceLevel } from './strategyStatusUtils';
import { tradingApi } from '../../services/api';
import { isValidHistoricalBar } from '../../market/historicalBars';
import StrategySymbolChartControls, { type StrategyChartStyle } from './StrategySymbolChartControls';
import { strategyChartLegend } from './strategySymbolChartPresentation';
import ChartFrame from '../Chart/ChartFrame';
import { LightweightChart, type ChartLineOverlay } from '../Chart/LightweightChart';
import {
    average,
    chartTimeFor,
    consolidateDailyBars,
    dailyManagedOrbMarkers,
    durationForStrategy,
    groupByNyDay,
    latestWindowBars,
    managedOrbVolumeData,
    potentialManagedOrbMarkers,
    regularSessionBars,
    sessionSegmentBounds,
    smaData,
    timeframeSeconds,
    type ChartBar,
    type StrategyChartKind,
    type StrategyChartTimeframe,
} from './strategySymbolChartData';

interface StrategySymbolChartProps {
    symbol: string;
    timeframe: StrategyChartTimeframe;
    onTimeframeChange?: (timeframe: '5min' | '1d') => void;
    label: string;
    strategy: StrategyChartKind;
    status?: any;
    historicalSignals?: any[];
}

const isoDateForTime = (time: number) => new Date(time * 1000).toISOString();

const isoDateForChartTime = (time: ReturnType<typeof chartTimeFor>) => {
    if (typeof time === 'number') return isoDateForTime(time);
    if (typeof time === 'string') return time;
    const month = String(time.month).padStart(2, '0');
    const day = String(time.day).padStart(2, '0');
    return `${time.year}-${month}-${day}`;
};

const createStrategyOverlays = (
    bars: ChartBar[],
    timeframe: StrategyChartTimeframe,
    strategy: StrategyChartKind,
    status: any,
): ChartLineOverlay[] => {
    const overlays: ChartLineOverlay[] = [];
    const addSegment = (from: number, to: number, value: number, color: string) => {
        if (!Number.isFinite(value) || from >= to) return;
        overlays.push({
            color,
            lineWidth: 2,
            data: [
                { date: isoDateForTime(from), value },
                { date: isoDateForTime(to), value },
            ],
        });
    };
    const addLine = (data: Array<{ time: any; value: number }>, color: string, lineWidth: 1 | 2 | 3 | 4 = 2) => {
        if (!data.length) return;
        overlays.push({
            color,
            lineWidth,
            data: data.map((point) => ({ date: isoDateForChartTime(point.time), value: point.value })),
        });
    };

    if (strategy === 'ORB') {
        groupByNyDay(bars).forEach(({ bars: dayBars }) => {
            const sessionBars = regularSessionBars(dayBars);
            if (sessionBars.length < 2) return;
            const openingBar = sessionBars[0];
            const { from, to } = sessionSegmentBounds(sessionBars, timeframe);
            addSegment(from, to, openingBar.high, themeColors.success);
            addSegment(from, to, openingBar.low, themeColors.dangerLight);
        });

        const dayGroups = groupByNyDay(bars);
        const latestDay = dayGroups[dayGroups.length - 1];
        const latestSessionBars = latestDay ? regularSessionBars(latestDay.bars) : [];
        if (
            latestSessionBars.length > 1 &&
            hasFreshLevels(status) &&
            isCurrentNySessionDate(status) &&
            isValidPriceLevel(status?.resistance) &&
            isValidPriceLevel(status?.support)
        ) {
            const { from, to } = sessionSegmentBounds(latestSessionBars, timeframe);
            addSegment(from, to, Number(status.resistance), themeColors.neon);
            addSegment(from, to, Number(status.support), themeColors.warningBright);
        }
    }

    if (strategy === 'DT') {
        const dayGroups = groupByNyDay(bars);
        const dailyTrueRanges: number[] = [];
        let previousClose: number | null = null;
        const thresholdFactor = Number(status?.threshold_factor || 0.5);

        dayGroups.forEach(({ bars: dayBars }, dayIndex) => {
            const sessionBars = regularSessionBars(dayBars);
            if (sessionBars.length < 2) return;

            const high = Math.max(...sessionBars.map((bar) => bar.high));
            const low = Math.min(...sessionBars.map((bar) => bar.low));
            const close = sessionBars[sessionBars.length - 1].close;
            const trueRange = previousClose == null
                ? high - low
                : Math.max(high - low, Math.abs(high - previousClose), Math.abs(low - previousClose));
            const historicalAtr = average(dailyTrueRanges.slice(-14));
            dailyTrueRanges.push(trueRange);
            previousClose = close;

            const { from, to } = sessionSegmentBounds(sessionBars, timeframe);
            const isLatestDay = dayIndex === dayGroups.length - 1;
            const hasFreshStatusLevels = isLatestDay && hasFreshLevels(status) && isCurrentNySessionDate(status);
            const dailyOpen = hasFreshStatusLevels && isValidPriceLevel(status?.daily_open)
                ? Number(status.daily_open)
                : sessionBars[0].open;
            const threshold = hasFreshStatusLevels && Number.isFinite(Number(status?.intraday_threshold)) && Number(status?.intraday_threshold) > 0
                ? Number(status.intraday_threshold)
                : historicalAtr == null
                    ? null
                    : historicalAtr * thresholdFactor;

            addSegment(from, to, dailyOpen, themeColors.info);
            if (threshold != null && Number.isFinite(threshold)) {
                addSegment(from, to, dailyOpen + threshold, themeColors.success);
                addSegment(from, to, dailyOpen - threshold, themeColors.dangerLight);
            }
        });
    }

    if (strategy === 'ON') {
        addLine(smaData(bars, 30), themeColors.success, 1);
        addLine(smaData(bars, 200), themeColors.warningBright, 1);
    }

    if (strategy === 'MORB') {
        if (timeframe === '1d') {
            addLine(smaData(bars, Number(status?.entry_sma_period || 50), timeframe), themeColors.warning, 1);
            addLine(smaData(bars, Number(status?.sma_period || 10), timeframe), themeColors.warningBright, 1);
        } else {
            const entryMinutes = Number(status?.entry_minutes || 10);
            const atrMultiple = Number(status?.atr_multiple || 0.5);
            const direction = String(status?.direction || 'LONG').toUpperCase();
            const rangeBars = Math.max(1, Math.ceil(entryMinutes / Math.max(1, timeframeSeconds(timeframe) / 60)));
            const dayGroups = groupByNyDay(bars);
            let previousClose: number | null = null;
            const trueRanges: number[] = [];

            dayGroups.forEach(({ bars: dayBars }, dayIndex) => {
                const sessionBars = regularSessionBars(dayBars);
                if (sessionBars.length <= rangeBars) return;
                const openingBars = sessionBars.slice(0, rangeBars);
                const high = Math.max(...openingBars.map((bar) => bar.high));
                const low = Math.min(...openingBars.map((bar) => bar.low));
                const dayHigh = Math.max(...sessionBars.map((bar) => bar.high));
                const dayLow = Math.min(...sessionBars.map((bar) => bar.low));
                const dayClose = sessionBars[sessionBars.length - 1].close;
                const trueRange = previousClose == null
                    ? dayHigh - dayLow
                    : Math.max(dayHigh - dayLow, Math.abs(dayHigh - previousClose), Math.abs(dayLow - previousClose));
                const historicalAtr = average(trueRanges.slice(-14));
                trueRanges.push(trueRange);
                previousClose = dayClose;
                const configuredAtr = Number(status?.atr);
                const atr = dayIndex === dayGroups.length - 1 && Number.isFinite(configuredAtr) && configuredAtr > 0
                    ? configuredAtr
                    : (historicalAtr ?? trueRange);
                const { from, to } = sessionSegmentBounds(sessionBars, timeframe);
                addSegment(from, to, openingBars[0].open, themeColors.info);
                addSegment(from, to, high, themeColors.success);
                addSegment(from, to, low, themeColors.dangerLight);
                if (Number.isFinite(atr) && atr > 0) {
                    const trigger = direction === 'SHORT' ? low - atr * atrMultiple : high + atr * atrMultiple;
                    addSegment(from, to, trigger, themeColors.warningBright);
                }
            });

            const latestDay = dayGroups[dayGroups.length - 1];
            const latestSession = latestDay ? regularSessionBars(latestDay.bars) : [];
            if (latestSession.length > 1) {
                const { from, to } = sessionSegmentBounds(latestSession, timeframe);
                const addStatusLevel = (value: any, color: string) => {
                    if (isValidPriceLevel(value)) addSegment(from, to, Number(value), color);
                };
                addStatusLevel(status?.daily_open, themeColors.info);
                addStatusLevel(status?.entry_level, themeColors.neon);
                addStatusLevel(status?.entry_sma, themeColors.warning);
                addStatusLevel(status?.ten_sma, themeColors.warningBright);
                (Array.isArray(status?.simulated_stop_levels) ? status.simulated_stop_levels : [])
                    .forEach((value: any) => addStatusLevel(value, themeColors.danger));
            }
        }
    }

    return overlays;
};

const StrategySymbolChart: React.FC<StrategySymbolChartProps> = ({ symbol, timeframe, onTimeframeChange, label, strategy, status, historicalSignals = [] }) => {
    const { token } = useAuth();
    const [bars, setBars] = useState<ChartBar[]>([]);
    const [chartStyle, setChartStyle] = useState<StrategyChartStyle>('candles');
    const [showMarkers, setShowMarkers] = useState(false);
    const [showExtendedHours, setShowExtendedHours] = useState(false);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        if (timeframe === '1d' && chartStyle !== 'candles') setChartStyle('candles');
    }, [timeframe, chartStyle]);

    useEffect(() => {
        if (!token) return undefined;
        const controller = new AbortController();
        const fetchHistory = async () => {
            setBars([]);
            setLoading(true);
            setError(null);
            try {
                const result = await tradingApi.getHistoricalData(
                    symbol,
                    timeframe,
                    durationForStrategy(timeframe, strategy),
                    'auto',
                    controller.signal,
                );
                if (!result.success || !Array.isArray(result.data)) {
                    throw new Error(result.error || 'No chart data returned');
                }

                const loadedBars: ChartBar[] = result.data
                    .filter(isValidHistoricalBar)
                    .map((bar) => ({
                        time: bar.time,
                        open: bar.open,
                        high: bar.high,
                        low: bar.low,
                        close: bar.close,
                        volume: bar.volume ?? 0,
                    }))
                    .sort((left, right) => left.time - right.time);
                setBars(timeframe === '1d' ? consolidateDailyBars(loadedBars) : loadedBars);
            } catch (err) {
                if (!controller.signal.aborted) {
                    setBars([]);
                    setError(err instanceof Error ? err.message : 'Failed to load chart');
                }
            } finally {
                if (!controller.signal.aborted) setLoading(false);
            }
        };

        fetchHistory();
        return () => controller.abort();
    }, [symbol, timeframe, token, strategy]);

    const displayBars = useMemo(
        () => timeframe === '1d' || showExtendedHours ? bars : regularSessionBars(bars),
        [bars, timeframe, showExtendedHours],
    );
    const chartData = useMemo(() => displayBars.map((bar) => ({
        date: isoDateForTime(bar.time),
        open: bar.open,
        high: bar.high,
        low: bar.low,
        close: bar.close,
        volume: bar.volume,
    })), [displayBars]);
    const lineOverlays = useMemo(
        () => createStrategyOverlays(displayBars, timeframe, strategy, status),
        [displayBars, timeframe, strategy, status],
    );
    const volumeData = useMemo(() => {
        const values = strategy === 'MORB'
            ? managedOrbVolumeData(displayBars, status, themeColors)
            : displayBars.map((bar) => ({
                time: chartTimeFor(bar.time, timeframe),
                value: Number(bar.volume || 0),
                color: Number(bar.close) >= Number(bar.open) ? themeColors.success : themeColors.danger,
            }));
        return timeframe === '1d' ? [] : values.map((point) => ({
            date: isoDateForChartTime(point.time),
            value: point.value,
            color: point.color,
        }));
    }, [displayBars, timeframe, strategy, status]);
    const customMarkers = useMemo(() => {
        if (strategy !== 'MORB' || !showMarkers) return [];
        const markers = timeframe === '1d'
            ? dailyManagedOrbMarkers(displayBars, status, themeColors, historicalSignals)
            : potentialManagedOrbMarkers(displayBars, status, timeframe, themeColors);
        return markers.map((marker) => ({
            ...marker,
            date: isoDateForChartTime(marker.time),
        }));
    }, [displayBars, timeframe, strategy, status, historicalSignals, showMarkers]);

    const legend = strategyChartLegend(strategy, timeframe, status, themeColors);
    const latestBars = latestWindowBars(timeframe);

    return (
        <ChartFrame
            className="w-full border-t border-white/10"
            toolbar={(
                <StrategySymbolChartControls
                    symbol={symbol}
                    label={label}
                    strategy={strategy}
                    timeframe={timeframe}
                    onTimeframeChange={onTimeframeChange}
                    chartStyle={chartStyle}
                    showMarkers={showMarkers}
                    showExtendedHours={showExtendedHours}
                    legend={legend}
                    onChartStyleChange={setChartStyle}
                    onToggleMarkers={() => setShowMarkers((current) => !current)}
                    onToggleExtendedHours={() => setShowExtendedHours((current) => !current)}
                />
            )}
        >
            <div className="relative h-[320px]">
                <LightweightChart
                    key={`${symbol}:${timeframe}:${strategy}`}
                    data={chartData}
                    volumeData={volumeData}
                    lineOverlays={lineOverlays}
                    customMarkers={customMarkers}
                    type="symbol"
                    symbolStyle={chartStyle}
                    lineColor={themeColors.neon}
                    resolution={timeframe === '1d' ? '1D' : 'native'}
                    timeVisible={timeframe !== '1d'}
                    initialVisibleBars={latestBars}
                    latestVisibleBars={latestBars}
                    showLatestControl
                    hideControls
                />
                {loading && (
                    <div className="absolute inset-0 flex items-center justify-center bg-[#0e0e12]/65">
                        <Loader2 className="h-5 w-5 animate-spin text-neon" />
                    </div>
                )}
                {error && (
                    <div className="absolute inset-0 flex items-center justify-center bg-[#0e0e12]/90 px-4 text-center text-sm text-rose-300">
                        {error}
                    </div>
                )}
            </div>
        </ChartFrame>
    );
};

export default StrategySymbolChart;
